import React, { useState, useEffect } from 'react';
import {
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { Group as GroupIcon } from '@mui/icons-material';
import { Conversation } from '../../types/message';
import { useAuth } from '../../contexts/AuthContext';
import { UserService } from '../../services/userService';
import { formatDistanceToNow } from 'date-fns';

interface ConversationListItemProps {
  conversation: Conversation;
  isActive: boolean;
  onClick: () => void;
}

/**
 * ConversationListItem Component
 *
 * Individual conversation item in the list with:
 * - Avatar (user or group icon)
 * - Conversation name
 * - Last message preview
 * - Unread count badge
 * - Timestamp
 * - Muted indicator
 */
export const ConversationListItem: React.FC<ConversationListItemProps> = ({
  conversation,
  isActive,
  onClick,
}) => {
  const { authState } = useAuth();
  const user = authState.user;
  const [otherUserName, setOtherUserName] = useState<string | null>(null);

  // Fetch other user's name for direct messages
  useEffect(() => {
    const fetchOtherUserName = async () => {
      if (user && conversation.type === 'direct') {
        const otherUserId = conversation.participants.find((id) => id !== user.uid);
        if (otherUserId) {
          const otherUser = await UserService.getUserById(otherUserId);
          if (otherUser) {
            setOtherUserName(otherUser.displayName || otherUser.email || 'Unknown User');
          }
        }
      }
    };

    fetchOtherUserName();
  }, [conversation, user]);

  if (!user) return null;

  const memberInfo = conversation.memberInfo[user.uid];
  const unreadCount = memberInfo?.unreadCount || 0;
  const isMuted = memberInfo?.muted || false;

  // Get conversation display name
  const getConversationName = (): string => {
    if (conversation.type === 'group') {
      return conversation.name || 'Unnamed Group';
    }

    // For direct messages, show the fetched user name or fallback to user ID
    if (otherUserName) {
      return otherUserName;
    }

    const otherUserId = conversation.participants.find((id) => id !== user.uid);
    return otherUserId || 'Unknown User';
  };

  // Get avatar letter
  const getAvatarLetter = (): string => {
    const name = getConversationName();
    return name[0].toUpperCase();
  };

  // Format last message preview
  const getLastMessagePreview = (): string => {
    if (!conversation.lastMessage) {
      return 'No messages yet';
    }

    const { text, senderId } = conversation.lastMessage;
    const isOwnMessage = senderId === user.uid;
    const prefix = isOwnMessage ? 'You: ' : '';

    return `${prefix}${text.substring(0, 50)}${text.length > 50 ? '...' : ''}`;
  };

  return (
    <ListItem disablePadding>
      <ListItemButton
        selected={isActive}
        onClick={onClick}
        sx={{
          bgcolor: isActive ? 'action.selected' : 'transparent',
          '&:hover': {
            bgcolor: isActive ? 'action.selected' : 'action.hover',
          },
        }}
      >
        <ListItemAvatar>
          <Badge
            badgeContent={unreadCount}
            color="primary"
            max={99}
            invisible={unreadCount === 0}
          >
            <Avatar>
              {conversation.type === 'group' ? (
                <GroupIcon />
              ) : (
                getAvatarLetter()
              )}
            </Avatar>
          </Badge>
        </ListItemAvatar>

        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: unreadCount > 0 ? 600 : 400,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {getConversationName()}
              </Typography>
              {isMuted && (
                <Chip label="Muted" size="small" variant="outlined" sx={{ height: 20 }} />
              )}
            </Box>
          }
          secondary={
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  fontWeight: unreadCount > 0 ? 500 : 400,
                }}
              >
                {getLastMessagePreview()}
              </Typography>
              {conversation.lastMessage && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {formatDistanceToNow(conversation.lastMessage.timestamp, {
                    addSuffix: false,
                  })}
                </Typography>
              )}
            </Box>
          }
        />
      </ListItemButton>
    </ListItem>
  );
};
